import type { AssessmentStatus, AssessmentReason, FinancialBasis } from "./financial-availability.js";
import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum, uniqueIndex, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { accounts, contacts } from "./schema-crm.js";  // Import parent table references

// ============================================================================
// BUSINESS ANALYTICS MODULE - SPRINT 6A (v1.1.0)
// ============================================================================
// Tables: 25 total (matches 10-BUSINESS-ANALYTICS.sql migration + Industry Intelligence)
// Purpose: Account-level financial analysis, P&L/BS/CF, ratios, health scores, industry benchmarks
// CRITICAL: This schema MUST match docs/schemas/10-BUSINESS-ANALYTICS.sql exactly
// ============================================================================

// ============================================================================
// ENUMS - Financial Data (EXACT match to migration SQL)
// ============================================================================

export const baFinancialTemplateEnum = pgEnum("ba_financial_template", [
  "trading",           // Full P&L, BS, Cashflow - for operating businesses
  "investment",        // Simplified P&L/BS, no Cashflow - for holding entities
  "smsf"               // SMSF-specific template with member tracking
]);

export const baPeriodTypeEnum = pgEnum("ba_period_type", [
  "historical",        // Prior completed financial years
  "ytd",               // Year to date (current partial year)
  "forecast"           // Future projections
]);

export const baDataSourceEnum = pgEnum("ba_data_source", [
  "manual",            // Broker manual entry
  "accounting_xero",   // Imported from Xero
  "accounting_myob",   // Imported from MYOB
  "bank_feed",         // Aggregated from bank statements
  "ai_extracted",      // AI extraction from documents
  "document_parsed"    // Manual document parsing
]);

// Sprint 10: Data Classification & Trust Grade Enums
export const baDataClassificationEnum = pgEnum("ba_data_classification", [
  "accountant_prepared",
  "tax_return",
  "management_accounts",
  "xero_finalised",
  "xero_draft"
]);

export const baTrustGradeEnum = pgEnum("ba_trust_grade", [
  "credit_grade",
  "insight_grade"
]);

export const confidenceStatusEnum = pgEnum("ba_confidence_status", [
  "pending",
  "verified",
  "discrepancy",
  "manual_override",
  "legacy",
]);

// P&L Line Item ENUM (84 values — original 21 + 63 added in Sprint 13 F002)
export const baPlLineItemEnum = pgEnum("ba_pl_line_item", [
  // Revenue (original)
  "income_sales",
  "other_income",
  // Revenue (Sprint 13)
  "gross_revenue_sales",
  "less_returns_allowances",
  "less_discounts",
  // Cost of Sales (original)
  "cost_of_goods_sold",
  // Cost of Goods Sold (Sprint 13)
  "materials_purchases",
  "direct_labour",
  "manufacturing_overhead",
  "freight_delivery",
  "subcontractor_costs",
  "inventory_adjustments",
  // Operating Expenses (original)
  "wages_salaries",
  "rent_premises",
  "utilities",
  "insurance",
  "marketing",
  "professional_fees",
  "motor_vehicle_expenses",
  "office_expenses",
  "other_operating_expenses",
  // Personnel (Sprint 13)
  "salaries_wages",
  "bonuses_commissions",
  "superannuation",
  "payroll_tax",
  "workers_comp",
  "training_development",
  "recruitment",
  "employee_benefits",
  "contractors",
  // Occupancy (Sprint 13)
  "property_insurance",
  "repairs_maintenance",
  "cleaning",
  "rates_levies",
  // Admin & Office (Sprint 13)
  "office_supplies",
  "printing_stationery",
  "phone_internet",
  "it_software",
  "it_hardware",
  "it_support",
  "bank_fees",
  "accounting_fees",
  "legal_fees",
  "audit_fees",
  "consulting_fees",
  // Sales & Marketing (Sprint 13)
  "advertising",
  "digital_seo",
  "website_hosting",
  "sponsorships",
  "travel_entertainment",
  "client_gifts",
  "crm_tools",
  "referral_fees",
  // Motor Vehicle (Sprint 13)
  "vehicle_lease_finance",
  "fuel",
  "rego_insurance",
  "vehicle_repairs",
  "tolls_parking",
  // Insurance (Sprint 13)
  "pi_insurance",
  "pl_insurance",
  "business_pack_insurance",
  "cyber_insurance",
  "key_person_insurance",
  // Compliance (Sprint 13)
  "asic_regulatory",
  "memberships_subscriptions",
  "licence_renewals",
  "cpd_training",
  // Other Operating Expenses (Sprint 13)
  "bad_debts",
  "donations",
  "sundry_expenses",
  // Non-Operating (original)
  "depreciation",
  "amortisation",
  "interest_expense",
  "lease_expense",
  "tax_expense",
  // Non-Operating (Sprint 13)
  "interest_income",
  "gain_loss_disposal",
  "fx_gain_loss",
  "other_non_operating_income",
  "other_non_operating_expense",
  // SMSF-Specific (original)
  "rental_income_smsf",
  "investment_income_smsf",
  "member_contributions",
  "fund_expenses"
]);

// Balance Sheet Line Item ENUM (36 values from migration)
export const baBsLineItemEnum = pgEnum("ba_bs_line_item", [
  // Current Assets - Individual
  "cash_business_account",
  "accounts_receivable",
  "inventory",
  "prepayments",
  "other_current_assets",
  // Non-Current Assets - Individual
  "property_plant_equipment",
  "investments",
  "intangibles",
  "goodwill",
  "other_non_current_assets",
  // Current Liabilities - Individual
  "accounts_payable",
  "short_term_debt",
  "tax_payable",
  "provisions",
  "other_current_liabilities",
  // Non-Current Liabilities - Individual
  "long_term_debt",
  "deferred_tax_liabilities",
  "other_non_current_liabilities",
  // Equity - Individual
  "share_capital",
  "retained_earnings",
  "reserves",
  "other_equity",
  // Aged Debtors/Creditors (for ratio calculations)
  "aged_debtors_30_plus",
  "aged_creditors_30_plus",
  // SMSF-Specific
  "cash_holdings_smsf",
  "property_values_smsf",
  "other_assets_smsf",
  "loan_balance_smsf",
  // Aggregate Totals (for tests and calculations)
  "current_assets",
  "non_current_assets",
  "total_assets",
  "current_liabilities",
  "non_current_liabilities",
  "total_liabilities",
  "total_equity",
  "cash",
  // Sprint 14: Current Assets (6)
  "trade_receivables",
  "related_party_receivables",
  "gst_receivable",
  "work_in_progress",
  "deposits_paid",
  "loans_to_directors",
  // Sprint 14: Non-Current Assets (7)
  "land_and_buildings",
  "plant_and_machinery",
  "motor_vehicles",
  "furniture_and_fittings",
  "right_of_use_assets",
  "investments_in_subsidiaries",
  "deferred_tax_assets",
  // Sprint 14: Current Liabilities (7)
  "gst_payable",
  "payg_withholding",
  "superannuation_payable",
  "related_party_payables",
  "current_portion_long_term_debt",
  "lease_liabilities_current",
  "employee_entitlements_current",
  // Sprint 14: Non-Current Liabilities (4)
  "lease_liabilities_non_current",
  "related_party_loans",
  "employee_entitlements_non_current",
  "loans_from_directors",
  // Sprint 14: Equity (3)
  "current_year_profit_loss",
  "revaluation_reserve",
  "drawings",
]);

// Cashflow Line Item ENUM (12 values from migration)
export const baCfLineItemEnum = pgEnum("ba_cf_line_item", [
  // Operating Activities
  "operating_receipts",
  "operating_payments",
  "interest_paid",
  "tax_paid",
  // Investing Activities
  "capital_expenditure",
  "proceeds_from_asset_sales",
  "investments_made",
  "proceeds_from_investments",
  // Financing Activities
  "loan_drawdowns",
  "loan_repayments",
  "equity_raised",
  "dividends_paid"
]);

// Normalisation Adjustment Types (13 values from migration)
export const baNormalisationTypeEnum = pgEnum("ba_normalisation_type", [
  "one_off_income",           // Remove from EBITDA (negative adjustment)
  "one_off_expenses",         // Add back to EBITDA (positive adjustment)
  "director_drawings",        // Add back to EBITDA
  "related_party_rent",       // Adjust to market rate
  "related_party_wages",      // Adjust to market rate
  "related_party_other",      // Other related party adjustments
  "personal_expenses",        // Add back personal expenses through business
  "non_recurring_income",     // Remove non-recurring income
  "covid_impact",             // COVID-related adjustments
  "depreciation_addback",     // Add back depreciation
  "amortisation_addback",     // Add back amortisation
  "custom"                    // Freeform custom adjustment
]);

// Financial Ratio Types (24 values from migration)
export const baRatioTypeEnum = pgEnum("ba_ratio_type", [
  // P&L Ratios
  "revenue_growth",              // (Current Revenue / Previous Revenue) - 1
  "cost_growth",                 // (Current Expenses / Previous Expenses) - 1
  "npat_growth",                 // (Current Net Profit / Previous Net Profit) - 1
  "gross_profit_margin",         // Gross Profit / Revenue
  "ebitda_margin",               // Adjusted EBITDA / Revenue
  "net_profit_margin",           // Net Profit / Revenue
  // Return Ratios
  "return_on_assets",            // Net Profit / Average Total Assets
  "return_on_equity",            // Net Profit / Average Net Assets
  // Coverage Ratios
  "interest_coverage_ratio",     // Adjusted EBIT / Interest Expense
  "ebitda_to_interest",          // Adjusted EBITDA / Interest Expense
  "debt_service_coverage",       // Cash Available / Debt Service
  // Liquidity Ratios
  "current_ratio",               // Current Assets / Current Liabilities
  "quick_ratio",                 // (Current Assets - Inventory) / Current Liabilities
  "cash_ratio",                  // Cash / Current Liabilities
  // Leverage Ratios
  "debt_to_equity",              // Total Debt / Net Assets
  "net_debt_to_ebitda",          // Net Debt / Adjusted EBITDA
  // Efficiency Ratios
  "inventory_turnover",          // COGS / Average Inventory
  "days_sales_outstanding",      // Aged Debtors / (Revenue / 365)
  "days_payable_outstanding",    // Aged Creditors / (COGS / 365)
  "working_capital_turnover",    // Revenue / Average Working Capital
  // SMSF-Specific
  "smsf_liquidity_assets",       // Liquid Assets / Total Assets
  "smsf_liquidity_properties",   // Cash / Property Values
  "smsf_lvr",                    // Loan / Property Value
  "smsf_icr"                     // Income / Interest Expense
]);

export const baHealthRatingEnum = pgEnum("ba_health_rating", [
  "excellent",    // 80-100 score
  "good",         // 60-79 score
  "fair",         // 40-59 score
  "poor",         // 20-39 score
  "critical"      // 0-19 score
]);

export const baTrendDirectionEnum = pgEnum("ba_trend_direction", [
  "improving",
  "stable",
  "declining"
]);

export const baConnectionStatusEnum = pgEnum("ba_connection_status", [
  "active",
  "expired",
  "revoked",
  "error"
]);

export const baSyncStatusEnum = pgEnum("ba_sync_status", [
  "pending",
  "in_progress",
  "completed",
  "failed"
]);

// Industry Intelligence ENUMs (Sprint 6A)
export const crmXIndLevelEnum = pgEnum("crm_x_ind_level_enum", ["low", "medium", "high"]);
export const crmXIndTrendEnum = pgEnum("crm_x_ind_trend_enum", ["increasing", "steady", "decreasing"]);
export const crmXIndLifecycleEnum = pgEnum("crm_x_ind_lifecycle_enum", ["startup", "growth", "maturity", "decline"]);
export const crmXIndImpactEnum = pgEnum("crm_x_ind_impact_enum", ["positive", "negative", "neutral"]);

// ============================================================================
// 1. Entity Financial Records (Master Container)
// ============================================================================

export const baEntityFinancialRecords = pgTable("crm_x_ba_entity_financial_records", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Entity Reference
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "restrict" }).notNull(),

  // Template Configuration
  financialTemplate: baFinancialTemplateEnum("financial_template").notNull().default("trading"),

  // Period Configuration
  lastFinancialYearEnd: date("last_financial_year_end"),
  ytdEndMonth: integer("ytd_end_month"),
  includeForecast: integer("include_forecast").default(0),

  // Data Source
  primaryDataSource: baDataSourceEnum("primary_data_source").default("manual"),
  sourceReference: text("source_reference"),

  // Validation Status
  isBalanced: integer("is_balanced").default(0),
  lastValidatedAt: timestamp("last_validated_at"),

  // Notes
  notes: text("notes"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// 2. Data Sources (Provenance Tracking)
// ============================================================================

export const baDataSources = pgTable("crm_x_ba_data_sources", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Entity Reference
  entityFinancialRecordId: uuid("entity_financial_record_id")
    .references(() => baEntityFinancialRecords.id, { onDelete: "cascade" })
    .notNull(),

  // Source Details
  sourceType: baDataSourceEnum("source_type").notNull(),
  sourceReference: text("source_reference"),
  sourceDate: date("source_date"),
  documentId: uuid("document_id"),

  // Notes
  notes: text("notes"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// 3. Financial Periods (Time Dimension)
// ============================================================================

export const baFinancialPeriods = pgTable("crm_x_ba_financial_periods", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Parent Reference
  entityFinancialRecordId: uuid("entity_financial_record_id")
    .references(() => baEntityFinancialRecords.id, { onDelete: "cascade" })
    .notNull(),

  // Period Definition
  periodType: baPeriodTypeEnum("period_type").notNull(),
  periodLabel: text("period_label").notNull(),
  periodStartDate: date("period_start_date").notNull(),
  periodEndDate: date("period_end_date").notNull(),
  monthsInPeriod: integer("months_in_period").notNull(),

  // Sequence for Ordering
  periodSequence: integer("period_sequence").notNull(),

  // Completeness
  isComplete: integer("is_complete").default(0),

  // Data Source
  dataSource: baDataSourceEnum("data_source").default("manual"),
  sourceDocumentReference: text("source_document_reference"),

  // Data Classification (Sprint 10)
  dataClassification: baDataClassificationEnum("data_classification").default("accountant_prepared"),
  trustGrade: baTrustGradeEnum("trust_grade").default("credit_grade"),
  annualisationFactor: numeric("annualisation_factor", { precision: 5, scale: 4 }),
  annualisedValues: integer("annualised_values").default(0),

  // Validation
  /*
   * confidence_status transition graph (M1.1; edges enforced in
   * server/services/financial/confidence-transitions.ts):
   * pending --validate_pass--> verified
   * pending --validate_fail--> discrepancy
   * discrepancy --override--> manual_override
   * discrepancy --re_extract--> pending
   * legacy --revalidate--> pending
   * verified --reopen--> pending
   * manual_override --reopen--> pending
   * C1 backfill is the only writer of legacy; no runtime edge enters legacy.
   */
  confidenceStatus: confidenceStatusEnum("confidence_status").notNull().default("pending"),
  verifiedBy: uuid("verified_by"),
  verifiedAt: timestamp("verified_at", { withTimezone: true }),
  isBalanced: integer("is_balanced").default(0),
  balanceDiscrepancy: numeric("balance_discrepancy", { precision: 15, scale: 2 }).default("0"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ============================================================================
// 4. Profit & Loss (v1.1.0 - with custom_label)
// ============================================================================

export const baProfitLoss = pgTable("crm_x_ba_profit_loss", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Period Reference
  financialPeriodId: uuid("financial_period_id")
    .references(() => baFinancialPeriods.id, { onDelete: "cascade" })
    .notNull(),

  // Line Item
  lineItem: baPlLineItemEnum("line_item").notNull(),
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull().default("0"),

  // Custom Label (NEW v1.1.0 - Sprint 6A)
  customLabel: text("custom_label"),
  sourceDocumentId: uuid("source_document_id"),
  sourcePage: integer("source_page"),
  sourceLineRef: text("source_line_ref"),
  extractionJobId: uuid("extraction_job_id"),

  // Notes
  notes: text("notes"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Preserve PostgreSQL microseconds in the optimistic-concurrency token.
  updatedAt: timestamp("updated_at", { mode: "string" }).defaultNow().notNull(),
});

// ============================================================================
// 5. Balance Sheet (v1.1.0 - with custom_label)
// ============================================================================

export const baBalanceSheet = pgTable("crm_x_ba_balance_sheet", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Period Reference
  financialPeriodId: uuid("financial_period_id")
    .references(() => baFinancialPeriods.id, { onDelete: "cascade" })
    .notNull(),

  // Line Item
  lineItem: baBsLineItemEnum("line_item").notNull(),
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull().default("0"),

  // Custom Label (NEW v1.1.0 - Sprint 6A)
  customLabel: text("custom_label"),
  sourceDocumentId: uuid("source_document_id"),
  sourcePage: integer("source_page"),
  sourceLineRef: text("source_line_ref"),
  extractionJobId: uuid("extraction_job_id"),

  // Notes
  notes: text("notes"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Preserve PostgreSQL microseconds in the optimistic-concurrency token.
  updatedAt: timestamp("updated_at", { mode: "string" }).defaultNow().notNull(),
});

// ============================================================================
// 6. Cash Flow (Auto-Generated + Manual Override)
// ============================================================================

export const baCashflow = pgTable("crm_x_ba_cashflow", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Period Reference
  financialPeriodId: uuid("financial_period_id")
    .references(() => baFinancialPeriods.id, { onDelete: "cascade" })
    .notNull(),

  // Line Item
  lineItem: baCfLineItemEnum("line_item").notNull(),
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull().default("0"),

  // Calculation Tracking
  isAutoCalculated: integer("is_auto_calculated").default(1),
  sourceDocumentId: uuid("source_document_id"),
  sourcePage: integer("source_page"),
  sourceLineRef: text("source_line_ref"),
  extractionJobId: uuid("extraction_job_id"),

  // Notes
  notes: text("notes"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ============================================================================
// 7. Normalisation Templates (Standard Adjustment Types)
// ============================================================================

export const baNormalisationTemplates = pgTable("crm_x_ba_normalisation_templates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Template Definition
  name: text("name").notNull(),
  adjustmentType: baNormalisationTypeEnum("adjustment_type").notNull(),
  description: text("description"),
  defaultDirection: text("default_direction"), // 'add' or 'remove'
  category: text("category"),

  // Status
  isActive: integer("is_active").default(1),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// ============================================================================
// 8. Normalisation Adjustments (EBITDA Addbacks/Removals)
// ============================================================================

export const baNormalisationAdjustments = pgTable("crm_x_ba_normalisation_adjustments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Period Reference
  financialPeriodId: uuid("financial_period_id")
    .references(() => baFinancialPeriods.id, { onDelete: "cascade" })
    .notNull(),

  // Template or Custom
  templateId: uuid("template_id").references(() => baNormalisationTemplates.id, { onDelete: "set null" }),
  customName: text("custom_name"),
  adjustmentType: baNormalisationTypeEnum("adjustment_type").notNull(),

  // Amount
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull(),
  direction: text("direction").notNull(), // 'add' or 'remove'

  // Documentation
  notes: text("notes"),
  supportingDocumentReference: text("supporting_document_reference"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// 9. Calculated Ratios (20+ Financial Ratios)
// ============================================================================

export const baCalculatedRatios = pgTable("crm_x_ba_calculated_ratios", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Period Reference
  financialPeriodId: uuid("financial_period_id")
    .references(() => baFinancialPeriods.id, { onDelete: "cascade" })
    .notNull(),

  // Ratio Details
  ratioType: baRatioTypeEnum("ratio_type").notNull(),
  ratioValue: numeric("ratio_value", { precision: 15, scale: 4 }),

  assessmentStatus: text("assessment_status").$type<AssessmentStatus>(),
  assessmentReasons: text("assessment_reasons").array().$type<AssessmentReason[]>(),
  calculationVersion: text("calculation_version"),
  basis: jsonb("basis").$type<Omit<FinancialBasis, "inputDigest">>(),
  inputHash: text("input_hash"),

  // Calculation Tracking
  formulaApplied: text("formula_applied"),
  calculationNotes: text("calculation_notes"),

  // Trend Analysis (compared to previous period)
  previousPeriodValue: numeric("previous_period_value", { precision: 15, scale: 4 }),
  changeAbsolute: numeric("change_absolute", { precision: 15, scale: 4 }),
  changePercentage: numeric("change_percentage", { precision: 8, scale: 4 }),
  trendDirection: baTrendDirectionEnum("trend_direction"),

  // Benchmark Comparison
  industryBenchmark: numeric("industry_benchmark", { precision: 15, scale: 4 }),
  benchmarkSource: text("benchmark_source"),
  varianceToBenchmark: numeric("variance_to_benchmark", { precision: 15, scale: 4 }),

  // Status Assessment
  status: text("status"), // 'strong', 'adequate', 'marginal', 'weak'

  // Audit Fields
  calculatedAt: timestamp("calculated_at").defaultNow().notNull(),
});

// ============================================================================
// 10. Health Scores (Composite Business Health)
// ============================================================================

export const baHealthScores = pgTable("crm_x_ba_health_scores", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Entity Reference (account-level, not period-level)
  entityFinancialRecordId: uuid("entity_financial_record_id")
    .references(() => baEntityFinancialRecords.id, { onDelete: "cascade" })
    .notNull(),

  // Assessment Period
  assessmentDate: date("assessment_date").notNull(),
  basedOnPeriodId: uuid("based_on_period_id").references(() => baFinancialPeriods.id, { onDelete: "set null" }),

  // Category Scores (0-100)
  liquidityScore: integer("liquidity_score"),
  leverageScore: integer("leverage_score"),
  profitabilityScore: integer("profitability_score"),
  efficiencyScore: integer("efficiency_score"),
  growthScore: integer("growth_score"),

  // Composite Score
  overallScore: integer("overall_score"),
  healthRating: baHealthRatingEnum("health_rating"),
  assessmentStatus: text("assessment_status").$type<AssessmentStatus>(),
  assessmentReasons: text("assessment_reasons").array().$type<AssessmentReason[]>(),
  calculationVersion: text("calculation_version"),
  basis: jsonb("basis").$type<Omit<FinancialBasis, "inputDigest">>(),
  inputHash: text("input_hash"),

  // Trend Analysis
  previousOverallScore: integer("previous_overall_score"),
  trendDirection: baTrendDirectionEnum("trend_direction"),

  // Insights (stored as arrays)
  keyConcerns: text("key_concerns").array(),
  keyStrengths: text("key_strengths").array(),
  recommendations: text("recommendations").array(),

  // Calculation Details
  methodologyNotes: text("methodology_notes"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// 11. Industries Master Table (IBISWorld Industry Intelligence)
// ============================================================================

export const baIndustries = pgTable("crm_x_ba_industries", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // IBIS Identification
  ibisCode: text("ibis_code").notNull().unique(),
  anzsicCode: text("anzsic_code").notNull(),
  anzsicDivision: text("anzsic_division"),
  industryName: text("industry_name").notNull(),

  // At a Glance Metrics
  revenueTotal: numeric("revenue_total", { precision: 15, scale: 2 }),
  revenueCagr5yr: numeric("revenue_cagr_5yr", { precision: 6, scale: 2 }),
  employeesCount: integer("employees_count"),
  enterprisesCount: integer("enterprises_count"),
  establishmentsCount: integer("establishments_count"),
  profitTotal: numeric("profit_total", { precision: 15, scale: 2 }),
  profitMargin: numeric("profit_margin", { precision: 6, scale: 2 }),
  wagesTotal: numeric("wages_total", { precision: 15, scale: 2 }),
  averageWage: numeric("average_wage", { precision: 12, scale: 2 }),

  // Industry Structure Characteristics (Level + Trend)
  concentrationLevel: crmXIndLevelEnum("concentration_level"),
  concentrationTrend: crmXIndTrendEnum("concentration_trend"),
  barriersToEntryLevel: crmXIndLevelEnum("barriers_to_entry_level"),
  barriersToEntryTrend: crmXIndTrendEnum("barriers_to_entry_trend"),
  regulationLevel: crmXIndLevelEnum("regulation_level"),
  regulationTrend: crmXIndTrendEnum("regulation_trend"),
  lifeCycleStage: crmXIndLifecycleEnum("life_cycle_stage"),
  volatilityLevel: crmXIndLevelEnum("volatility_level"),
  volatilityTrend: crmXIndTrendEnum("volatility_trend"),
  capitalIntensityLevel: crmXIndLevelEnum("capital_intensity_level"),
  capitalIntensityTrend: crmXIndTrendEnum("capital_intensity_trend"),
  competitionLevel: crmXIndLevelEnum("competition_level"),
  competitionTrend: crmXIndTrendEnum("competition_trend"),
  innovationLevel: crmXIndLevelEnum("innovation_level"),
  innovationTrend: crmXIndTrendEnum("innovation_trend"),

  // SWOT Analysis
  swotStrengths: text("swot_strengths").array(),
  swotWeaknesses: text("swot_weaknesses").array(),
  swotOpportunities: text("swot_opportunities").array(),
  swotThreats: text("swot_threats").array(),

  // Cost Structure Benchmarks (%)
  costWagesPct: numeric("cost_wages_pct", { precision: 6, scale: 2 }),
  costPurchasesPct: numeric("cost_purchases_pct", { precision: 6, scale: 2 }),
  costProfitPct: numeric("cost_profit_pct", { precision: 6, scale: 2 }),
  costDepreciationPct: numeric("cost_depreciation_pct", { precision: 6, scale: 2 }),
  costRentPct: numeric("cost_rent_pct", { precision: 6, scale: 2 }),
  costUtilitiesPct: numeric("cost_utilities_pct", { precision: 6, scale: 2 }),
  costOtherPct: numeric("cost_other_pct", { precision: 6, scale: 2 }),

  // Key Ratios
  revenuePerEmployee: numeric("revenue_per_employee", { precision: 12, scale: 2 }),
  revenuePerEnterprise: numeric("revenue_per_enterprise", { precision: 15, scale: 2 }),
  employeesPerEnterprise: numeric("employees_per_enterprise", { precision: 6, scale: 2 }),
  employeesPerEstablishment: numeric("employees_per_establishment", { precision: 6, scale: 2 }),
  establishmentsPerEnterprise: numeric("establishments_per_enterprise", { precision: 6, scale: 2 }),
  wagesToRevenuePct: numeric("wages_to_revenue_pct", { precision: 6, scale: 2 }),
  ivaTotal: numeric("iva_total", { precision: 15, scale: 2 }),
  ivaToRevenuePct: numeric("iva_to_revenue_pct", { precision: 6, scale: 2 }),

  // Growth Metrics
  revenueCagrForecast: numeric("revenue_cagr_forecast", { precision: 6, scale: 2 }),

  // Porter's Five Forces
  substitutesLevel: crmXIndLevelEnum("substitutes_level"),
  substitutesTrend: crmXIndTrendEnum("substitutes_trend"),
  buyerPowerLevel: crmXIndLevelEnum("buyer_power_level"),
  buyerPowerTrend: crmXIndTrendEnum("buyer_power_trend"),
  supplierPowerLevel: crmXIndLevelEnum("supplier_power_level"),
  supplierPowerTrend: crmXIndTrendEnum("supplier_power_trend"),

  // Government/Assistance
  assistanceLevel: crmXIndLevelEnum("assistance_level"),
  assistanceTrend: crmXIndTrendEnum("assistance_trend"),

  // Market Concentration
  top4MarketSharePct: numeric("top_4_market_share_pct", { precision: 6, scale: 2 }),

  // Trade Metrics
  exportsTotal: numeric("exports_total", { precision: 15, scale: 2 }),
  importsTotal: numeric("imports_total", { precision: 15, scale: 2 }),
  exportsRevenuePct: numeric("exports_revenue_pct", { precision: 6, scale: 2 }),
  importsDemandPct: numeric("imports_demand_pct", { precision: 6, scale: 2 }),
  tradeBalance: text("trade_balance"),

  // Executive Summary
  executiveSummary: text("executive_summary"),
  keySuccessFactors: text("key_success_factors").array(),

  // Source Tracking
  reportDate: date("report_date").notNull(),
  reportPeriod: text("report_period"),
  source: text("source").default("IBIS World"),
  sourceUrl: text("source_url"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: uuid("updated_by"),
});

// ============================================================================
// 12. Industry Products (Product/Service Segmentation)
// ============================================================================

export const baIndustryProducts = pgTable("crm_x_ba_industry_products", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Parent Reference
  industryId: uuid("industry_id")
    .references(() => baIndustries.id, { onDelete: "cascade" })
    .notNull(),

  // Product/Service Details
  productName: text("product_name").notNull(),
  revenueValue: numeric("revenue_value", { precision: 15, scale: 2 }),
  revenuePct: numeric("revenue_pct", { precision: 6, scale: 2 }),
  displayOrder: integer("display_order").default(0),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ============================================================================
// 13. Industry Markets (Market Segmentation)
// ============================================================================

export const baIndustryMarkets = pgTable("crm_x_ba_industry_markets", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Parent Reference
  industryId: uuid("industry_id")
    .references(() => baIndustries.id, { onDelete: "cascade" })
    .notNull(),

  // Market Segment Details
  marketName: text("market_name").notNull(),
  segmentType: text("segment_type").default("customer_type"),
  revenueValue: numeric("revenue_value", { precision: 15, scale: 2 }),
  revenuePct: numeric("revenue_pct", { precision: 6, scale: 2 }),
  displayOrder: integer("display_order").default(0),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ============================================================================
// 14. Industry Geography (Geographic Distribution)
// ============================================================================

export const baIndustryGeography = pgTable("crm_x_ba_industry_geography", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Parent Reference
  industryId: uuid("industry_id")
    .references(() => baIndustries.id, { onDelete: "cascade" })
    .notNull(),

  // Geographic Details
  stateTerritory: text("state_territory").notNull(),
  establishmentsPct: numeric("establishments_pct", { precision: 6, scale: 2 }),
  populationPct: numeric("population_pct", { precision: 6, scale: 2 }),
  concentrationIndex: numeric("concentration_index", { precision: 6, scale: 2 }),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ============================================================================
// 15. Industry Companies (Major Companies)
// ============================================================================

export const baIndustryCompanies = pgTable("crm_x_ba_industry_companies", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Parent Reference
  industryId: uuid("industry_id")
    .references(() => baIndustries.id, { onDelete: "cascade" })
    .notNull(),

  // Company Details
  companyName: text("company_name").notNull(),
  marketSharePct: numeric("market_share_pct", { precision: 6, scale: 2 }),
  revenueValue: numeric("revenue_value", { precision: 15, scale: 2 }),
  employeeCount: integer("employee_count"),
  establishmentCount: integer("establishment_count"),
  isTop4: integer("is_top_4").default(0),
  displayOrder: integer("display_order").default(0),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ============================================================================
// 16. Industry Drivers (External Drivers)
// ============================================================================

export const baIndustryDrivers = pgTable("crm_x_ba_industry_drivers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Parent Reference
  industryId: uuid("industry_id")
    .references(() => baIndustries.id, { onDelete: "cascade" })
    .notNull(),

  // Driver Details
  driverName: text("driver_name").notNull(),
  driverCategory: text("driver_category").default("macroeconomic"),
  impactDirection: crmXIndImpactEnum("impact_direction"),
  impactDescription: text("impact_description"),
  currentValue: text("current_value"),
  trendDirection: crmXIndTrendEnum("trend_direction"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ============================================================================
// 17. Industry Benchmarks
// ============================================================================

export const baIndustryBenchmarks = pgTable("crm_x_ba_industry_benchmarks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Industry Reference
  industryId: uuid("industry_id").references(() => baIndustries.id, { onDelete: "cascade" }),

  // Industry Classification (legacy)
  anzsicCode: text("anzsic_code").notNull(),
  industryName: text("industry_name").notNull(),

  // Metric Details
  metricName: text("metric_name").notNull(),

  // Values
  metricValue: numeric("metric_value", { precision: 15, scale: 4 }),
  metricLow: numeric("metric_low", { precision: 15, scale: 4 }),
  metricMedian: numeric("metric_median", { precision: 15, scale: 4 }),
  metricHigh: numeric("metric_high", { precision: 15, scale: 4 }),

  // Source Tracking
  source: text("source").notNull(),
  sourceReference: text("source_reference"),
  effectiveDate: date("effective_date").notNull(),
  expiryDate: date("expiry_date"),

  // Status
  isCurrent: integer("is_current").default(1),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// 18. SMSF Member Contributions
// ============================================================================

export const baSmsfMemberContributions = pgTable("crm_x_ba_smsf_member_contributions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Entity Reference
  entityFinancialRecordId: uuid("entity_financial_record_id")
    .references(() => baEntityFinancialRecords.id, { onDelete: "cascade" })
    .notNull(),

  // Member Details
  memberContactId: uuid("member_contact_id"),
  memberName: text("member_name").notNull(),

  // Period
  financialYear: text("financial_year").notNull(),

  // Contribution Amounts
  concessionalContributions: numeric("concessional_contributions", { precision: 15, scale: 2 }).default("0"),
  nonConcessionalContributions: numeric("non_concessional_contributions", { precision: 15, scale: 2 }).default("0"),

  // Audit Fields
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ============================================================================
// 19. Personal Financial Statements (Guarantor Support)
// ============================================================================

export const baPersonalFinancialStatements = pgTable("crm_x_ba_personal_financial_statements", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Person & Account Reference
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }).notNull(),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),

  // Statement Details
  statementDate: date("statement_date").notNull(),
  statementLabel: text("statement_label"),

  // ASSETS
  realEstate: numeric("real_estate", { precision: 15, scale: 2 }).default("0"),
  vehicles: numeric("vehicles", { precision: 15, scale: 2 }).default("0"),
  sharesInvestments: numeric("shares_investments", { precision: 15, scale: 2 }).default("0"),
  superannuation: numeric("superannuation", { precision: 15, scale: 2 }).default("0"),
  bankAccounts: numeric("bank_accounts", { precision: 15, scale: 2 }).default("0"),
  otherAssets: numeric("other_assets", { precision: 15, scale: 2 }).default("0"),
  totalAssets: numeric("total_assets", { precision: 15, scale: 2 }),

  // LIABILITIES
  mortgages: numeric("mortgages", { precision: 15, scale: 2 }).default("0"),
  personalLoans: numeric("personal_loans", { precision: 15, scale: 2 }).default("0"),
  creditCards: numeric("credit_cards", { precision: 15, scale: 2 }).default("0"),
  otherLiabilities: numeric("other_liabilities", { precision: 15, scale: 2 }).default("0"),
  totalLiabilities: numeric("total_liabilities", { precision: 15, scale: 2 }),

  // DERIVED
  netWorth: numeric("net_worth", { precision: 15, scale: 2 }),

  // INCOME (Annual)
  salaryIncome: numeric("salary_income", { precision: 15, scale: 2 }).default("0"),
  rentalIncome: numeric("rental_income", { precision: 15, scale: 2 }).default("0"),
  dividendIncome: numeric("dividend_income", { precision: 15, scale: 2 }).default("0"),
  otherIncome: numeric("other_income", { precision: 15, scale: 2 }).default("0"),
  totalIncome: numeric("total_income", { precision: 15, scale: 2 }),

  // PERSONAL DEBT SERVICING (Annual)
  mortgageRepayments: numeric("mortgage_repayments", { precision: 15, scale: 2 }).default("0"),
  personalLoanRepayments: numeric("personal_loan_repayments", { precision: 15, scale: 2 }).default("0"),
  creditCardMinimum: numeric("credit_card_minimum", { precision: 15, scale: 2 }).default("0"),
  otherCommitments: numeric("other_commitments", { precision: 15, scale: 2 }).default("0"),
  totalPersonalDebtService: numeric("total_personal_debt_service", { precision: 15, scale: 2 }),

  // DEBT SERVICING TOGGLE
  includeInServiceability: integer("include_in_serviceability").default(0),
  serviceabilityContributionType: text("serviceability_contribution_type"),

  // Source & Audit
  sourceType: baDataSourceEnum("source_type").notNull().default("manual"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// CUSTOM LINE ITEMS - Issue 222 TypeScript Fix
// ============================================================================

// Custom P&L Line Items (for user-defined categories)
export const baCustomPlItems = pgTable("crm_x_ba_custom_pl_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  financialPeriodId: uuid("financial_period_id").notNull(),
  itemName: varchar("item_name", { length: 255 }).notNull(),
  category: varchar("category", { length: 100 }).notNull(), // revenue, expense, adjustment
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull().default("0"),
  displayOrder: integer("display_order").default(0),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// Custom Balance Sheet Line Items (for user-defined categories)
export const baCustomBsItems = pgTable("crm_x_ba_custom_bs_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  financialPeriodId: uuid("financial_period_id").notNull(),
  itemName: varchar("item_name", { length: 255 }).notNull(),
  category: varchar("category", { length: 100 }).notNull(), // asset, liability, equity
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull().default("0"),
  displayOrder: integer("display_order").default(0),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// CUSTOM LINE ITEMS V2 - Sprint 11 (Entity-level definitions + per-period values)
// Replaces deprecated per-period baCustomPlItems / baCustomBsItems above
// ============================================================================

// Entity-level custom line item definitions (appear across ALL periods)
export const baCustomLineItems = pgTable("crm_x_ba_custom_line_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  entityFinancialRecordId: uuid("entity_financial_record_id").notNull(),
  statementType: text("statement_type").notNull(),
  category: text("category").notNull(),
  label: text("label").notNull(),
  subcategory: text('subcategory'),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Per-period amounts for custom line items
export const baCustomLineItemValues = pgTable("crm_x_ba_custom_line_item_values", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  customLineItemId: uuid("custom_line_item_id").notNull(),
  financialPeriodId: uuid("financial_period_id").notNull(),
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull().default("0"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ============================================================================
// ZODS SCHEMAS - Business Analytics
// ============================================================================

// Custom P&L Items
export const insertBaCustomPlItemSchema = createInsertSchema(baCustomPlItems).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaCustomPlItemSchema = insertBaCustomPlItemSchema.partial();
export const selectBaCustomPlItemSchema = createSelectSchema(baCustomPlItems);

// Custom BS Items
export const insertBaCustomBsItemSchema = createInsertSchema(baCustomBsItems).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaCustomBsItemSchema = insertBaCustomBsItemSchema.partial();
export const selectBaCustomBsItemSchema = createSelectSchema(baCustomBsItems);

// Custom Line Items V2 (Definitions)
export const insertBaCustomLineItemSchema = createInsertSchema(baCustomLineItems).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaCustomLineItemSchema = insertBaCustomLineItemSchema.partial().omit({
  entityFinancialRecordId: true,
  statementType: true
});
export const selectBaCustomLineItemSchema = createSelectSchema(baCustomLineItems);

// Custom Line Item Values V2 (Per-Period Amounts)
export const insertBaCustomLineItemValueSchema = createInsertSchema(baCustomLineItemValues).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaCustomLineItemValueSchema = insertBaCustomLineItemValueSchema.partial();
export const selectBaCustomLineItemValueSchema = createSelectSchema(baCustomLineItemValues);

// Type exports for Custom Line Items V2
export type BaCustomLineItem = typeof baCustomLineItems.$inferSelect;
export type InsertBaCustomLineItem = typeof baCustomLineItems.$inferInsert;
export type BaCustomLineItemValue = typeof baCustomLineItemValues.$inferSelect;
export type InsertBaCustomLineItemValue = typeof baCustomLineItemValues.$inferInsert;

// Entity Financial Records
export const insertBaEntityFinancialRecordSchema = createInsertSchema(baEntityFinancialRecords).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaEntityFinancialRecordSchema = insertBaEntityFinancialRecordSchema.partial();
export const selectBaEntityFinancialRecordSchema = createSelectSchema(baEntityFinancialRecords);

// Data Sources
export const insertBaDataSourceSchema = createInsertSchema(baDataSources).omit({ id: true, createdAt: true });
export const updateBaDataSourceSchema = insertBaDataSourceSchema.partial();
export const selectBaDataSourceSchema = createSelectSchema(baDataSources);

// Financial Periods
export const insertBaFinancialPeriodSchema = createInsertSchema(baFinancialPeriods).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaFinancialPeriodSchema = insertBaFinancialPeriodSchema.partial();
export const selectBaFinancialPeriodSchema = createSelectSchema(baFinancialPeriods);

// Profit & Loss
export const insertBaProfitLossSchema = createInsertSchema(baProfitLoss).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaProfitLossSchema = insertBaProfitLossSchema.partial();
export const selectBaProfitLossSchema = createSelectSchema(baProfitLoss);

// Balance Sheet
export const insertBaBalanceSheetSchema = createInsertSchema(baBalanceSheet).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaBalanceSheetSchema = insertBaBalanceSheetSchema.partial();
export const selectBaBalanceSheetSchema = createSelectSchema(baBalanceSheet);

// Cash Flow
export const insertBaCashflowSchema = createInsertSchema(baCashflow).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaCashflowSchema = insertBaCashflowSchema.partial();
export const selectBaCashflowSchema = createSelectSchema(baCashflow);

// Normalisation Templates
export const insertBaNormalisationTemplateSchema = createInsertSchema(baNormalisationTemplates).omit({
  id: true,
  createdAt: true
});
export const updateBaNormalisationTemplateSchema = insertBaNormalisationTemplateSchema.partial();
export const selectBaNormalisationTemplateSchema = createSelectSchema(baNormalisationTemplates);

// Normalisation Adjustments
export const insertBaNormalisationAdjustmentSchema = createInsertSchema(baNormalisationAdjustments).omit({
  id: true,
  createdAt: true
});
export const updateBaNormalisationAdjustmentSchema = insertBaNormalisationAdjustmentSchema.partial();
export const selectBaNormalisationAdjustmentSchema = createSelectSchema(baNormalisationAdjustments);

// Calculated Ratios
export const insertBaCalculatedRatioSchema = createInsertSchema(baCalculatedRatios).omit({
  id: true,
  calculatedAt: true
});
export const updateBaCalculatedRatioSchema = insertBaCalculatedRatioSchema.partial();
export const selectBaCalculatedRatioSchema = createSelectSchema(baCalculatedRatios);

// Health Scores
export const insertBaHealthScoreSchema = createInsertSchema(baHealthScores).omit({
  id: true,
  createdAt: true
});
export const updateBaHealthScoreSchema = insertBaHealthScoreSchema.partial();
export const selectBaHealthScoreSchema = createSelectSchema(baHealthScores);

// Industries
export const insertBaIndustrySchema = createInsertSchema(baIndustries).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaIndustrySchema = insertBaIndustrySchema.partial();
export const selectBaIndustrySchema = createSelectSchema(baIndustries);

// Industry Products
export const insertBaIndustryProductSchema = createInsertSchema(baIndustryProducts).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaIndustryProductSchema = insertBaIndustryProductSchema.partial();
export const selectBaIndustryProductSchema = createSelectSchema(baIndustryProducts);

// Industry Markets
export const insertBaIndustryMarketSchema = createInsertSchema(baIndustryMarkets).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaIndustryMarketSchema = insertBaIndustryMarketSchema.partial();
export const selectBaIndustryMarketSchema = createSelectSchema(baIndustryMarkets);

// Industry Geography
export const insertBaIndustryGeographySchema = createInsertSchema(baIndustryGeography).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaIndustryGeographySchema = insertBaIndustryGeographySchema.partial();
export const selectBaIndustryGeographySchema = createSelectSchema(baIndustryGeography);

// Industry Companies
export const insertBaIndustryCompanySchema = createInsertSchema(baIndustryCompanies).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaIndustryCompanySchema = insertBaIndustryCompanySchema.partial();
export const selectBaIndustryCompanySchema = createSelectSchema(baIndustryCompanies);

// Industry Drivers
export const insertBaIndustryDriverSchema = createInsertSchema(baIndustryDrivers).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaIndustryDriverSchema = insertBaIndustryDriverSchema.partial();
export const selectBaIndustryDriverSchema = createSelectSchema(baIndustryDrivers);

// Industry Benchmarks
export const insertBaIndustryBenchmarkSchema = createInsertSchema(baIndustryBenchmarks).omit({
  id: true,
  createdAt: true
});
export const updateBaIndustryBenchmarkSchema = insertBaIndustryBenchmarkSchema.partial();
export const selectBaIndustryBenchmarkSchema = createSelectSchema(baIndustryBenchmarks);

// SMSF Member Contributions
export const insertBaSmsfMemberContributionSchema = createInsertSchema(baSmsfMemberContributions).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaSmsfMemberContributionSchema = insertBaSmsfMemberContributionSchema.partial();
export const selectBaSmsfMemberContributionSchema = createSelectSchema(baSmsfMemberContributions);

// Personal Financial Statements
export const insertBaPersonalFinancialStatementSchema = createInsertSchema(baPersonalFinancialStatements).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateBaPersonalFinancialStatementSchema = insertBaPersonalFinancialStatementSchema.partial();
export const selectBaPersonalFinancialStatementSchema = createSelectSchema(baPersonalFinancialStatements);

// ============================================================================
// TYPES - Business Analytics
// ============================================================================

export type InsertBaEntityFinancialRecord = z.infer<typeof insertBaEntityFinancialRecordSchema>;
export type UpdateBaEntityFinancialRecord = z.infer<typeof updateBaEntityFinancialRecordSchema>;
export type BaEntityFinancialRecord = typeof baEntityFinancialRecords.$inferSelect;

export type InsertBaDataSource = z.infer<typeof insertBaDataSourceSchema>;
export type UpdateBaDataSource = z.infer<typeof updateBaDataSourceSchema>;
export type BaDataSource = typeof baDataSources.$inferSelect;

export type InsertBaFinancialPeriod = z.infer<typeof insertBaFinancialPeriodSchema>;
export type UpdateBaFinancialPeriod = z.infer<typeof updateBaFinancialPeriodSchema>;
export type BaFinancialPeriod = typeof baFinancialPeriods.$inferSelect;

export type InsertBaProfitLoss = z.infer<typeof insertBaProfitLossSchema>;
export type UpdateBaProfitLoss = z.infer<typeof updateBaProfitLossSchema>;
export type BaProfitLoss = typeof baProfitLoss.$inferSelect;

export type InsertBaBalanceSheet = z.infer<typeof insertBaBalanceSheetSchema>;
export type UpdateBaBalanceSheet = z.infer<typeof updateBaBalanceSheetSchema>;
export type BaBalanceSheet = typeof baBalanceSheet.$inferSelect;

export type InsertBaCashflow = z.infer<typeof insertBaCashflowSchema>;
export type UpdateBaCashflow = z.infer<typeof updateBaCashflowSchema>;
export type BaCashflow = typeof baCashflow.$inferSelect;

export type InsertBaNormalisationTemplate = z.infer<typeof insertBaNormalisationTemplateSchema>;
export type UpdateBaNormalisationTemplate = z.infer<typeof updateBaNormalisationTemplateSchema>;
export type BaNormalisationTemplate = typeof baNormalisationTemplates.$inferSelect;

export type InsertBaNormalisationAdjustment = z.infer<typeof insertBaNormalisationAdjustmentSchema>;
export type UpdateBaNormalisationAdjustment = z.infer<typeof updateBaNormalisationAdjustmentSchema>;
export type BaNormalisationAdjustment = typeof baNormalisationAdjustments.$inferSelect;

export type InsertBaCalculatedRatio = z.infer<typeof insertBaCalculatedRatioSchema>;
export type UpdateBaCalculatedRatio = z.infer<typeof updateBaCalculatedRatioSchema>;
export type BaCalculatedRatio = typeof baCalculatedRatios.$inferSelect;

export type InsertBaHealthScore = z.infer<typeof insertBaHealthScoreSchema>;
export type UpdateBaHealthScore = z.infer<typeof updateBaHealthScoreSchema>;
export type BaHealthScore = typeof baHealthScores.$inferSelect;

export type InsertBaIndustry = z.infer<typeof insertBaIndustrySchema>;
export type UpdateBaIndustry = z.infer<typeof updateBaIndustrySchema>;
export type BaIndustry = typeof baIndustries.$inferSelect;

export type InsertBaIndustryProduct = z.infer<typeof insertBaIndustryProductSchema>;
export type UpdateBaIndustryProduct = z.infer<typeof updateBaIndustryProductSchema>;
export type BaIndustryProduct = typeof baIndustryProducts.$inferSelect;

export type InsertBaIndustryMarket = z.infer<typeof insertBaIndustryMarketSchema>;
export type UpdateBaIndustryMarket = z.infer<typeof updateBaIndustryMarketSchema>;
export type BaIndustryMarket = typeof baIndustryMarkets.$inferSelect;

export type InsertBaIndustryGeography = z.infer<typeof insertBaIndustryGeographySchema>;
export type UpdateBaIndustryGeography = z.infer<typeof updateBaIndustryGeographySchema>;
export type BaIndustryGeography = typeof baIndustryGeography.$inferSelect;

export type InsertBaIndustryCompany = z.infer<typeof insertBaIndustryCompanySchema>;
export type UpdateBaIndustryCompany = z.infer<typeof updateBaIndustryCompanySchema>;
export type BaIndustryCompany = typeof baIndustryCompanies.$inferSelect;

export type InsertBaIndustryDriver = z.infer<typeof insertBaIndustryDriverSchema>;
export type UpdateBaIndustryDriver = z.infer<typeof updateBaIndustryDriverSchema>;
export type BaIndustryDriver = typeof baIndustryDrivers.$inferSelect;

export type InsertBaIndustryBenchmark = z.infer<typeof insertBaIndustryBenchmarkSchema>;
export type UpdateBaIndustryBenchmark = z.infer<typeof updateBaIndustryBenchmarkSchema>;
export type BaIndustryBenchmark = typeof baIndustryBenchmarks.$inferSelect;

export type InsertBaSmsfMemberContribution = z.infer<typeof insertBaSmsfMemberContributionSchema>;
export type UpdateBaSmsfMemberContribution = z.infer<typeof updateBaSmsfMemberContributionSchema>;
export type BaSmsfMemberContribution = typeof baSmsfMemberContributions.$inferSelect;

export type InsertBaPersonalFinancialStatement = z.infer<typeof insertBaPersonalFinancialStatementSchema>;
export type UpdateBaPersonalFinancialStatement = z.infer<typeof updateBaPersonalFinancialStatementSchema>;
export type BaPersonalFinancialStatement = typeof baPersonalFinancialStatements.$inferSelect;

// ============================================================================
// CSV ACCOUNT MAPPINGS - EDG-531 (Per-account import memory, Modano-style)
// ============================================================================

export const baCsvAccountMappings = pgTable("crm_x_ba_csv_account_mappings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").notNull(),
  sourceLabel: text("source_label").notNull(),
  destinationKind: text("destination_kind").notNull(),
  destinationEnumType: text("destination_enum_type"),
  destinationCustomItemId: uuid("destination_custom_item_id"),
  category: text("category").notNull(),
  statementType: text("statement_type").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uniqueAccountSource: uniqueIndex("ba_csv_account_mappings_account_source_idx")
    .on(table.accountId, table.sourceLabel),
}));

// Zod schemas for CSV Account Mappings
export const insertBaCsvAccountMappingSchema = createInsertSchema(baCsvAccountMappings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateBaCsvAccountMappingSchema = insertBaCsvAccountMappingSchema.partial();
export const selectBaCsvAccountMappingSchema = createSelectSchema(baCsvAccountMappings);

// Type exports
export type BaCsvAccountMapping = typeof baCsvAccountMappings.$inferSelect;
export type InsertBaCsvAccountMapping = typeof baCsvAccountMappings.$inferInsert;
